import { FC, ReactNode, useRef } from 'react'
import { useRouter } from 'next/router'
import { FirebaseAuth } from '@/services'
import { useOpen } from '@/hooks'
import { Dropdown } from '@/ui/views'
import { ImageOnError, ScrollTop, ImageOnDrag } from '@/helpers'
import { PlusIcon, UserIcon } from '@heroicons/react/16/solid'

import {
    CategoryState,
    ComponentState,
    SideoverState,
    UserState,
    TagState
} from '@/states'

import {
    COMPONENT_DEFUALT_FILTERS,
    COMPONENT_DEFAULT_ERRORS,
    COMPONENT_DEFAULT_FORM,
    TAG_DEFAULT_FORM,
    CATEGORY_DEFAULT_FORM,
    TAG_DEFAULT_ERRORS,
    CATEGORY_DEFAULT_ERRORS
} from '@/data'

const User: FC = (): ReactNode => {
    const { User, SetState } = UserState()
    const { open, setOpen, ref } = useOpen()
    const { push, pathname } = useRouter()

    const { SetState: SideoverSetState, Form, Errors } = SideoverState()
    const { SetState: CategorySetState } = CategoryState()
    const { SetState: TagSetState } = TagState()
    const { SetState: ComponentSetState } = ComponentState()

    const PreviousForm = useRef('component')

    const onLogout = async () => {
        ScrollTop()

        await FirebaseAuth.signOut()

        SetState({
            User: false,
            Loading: false
        })

        SideoverSetState({
            Form: COMPONENT_DEFAULT_FORM,
            Errors: COMPONENT_DEFAULT_ERRORS,
            Open: false,
            Loading: false
        })

        CategorySetState({
            Categories: [],
            Loading: false
        })

        TagSetState({
            Tags: [],
            Loading: false
        })

        ComponentSetState({
            Components: [],
            Loading: false,
            Filters: COMPONENT_DEFUALT_FILTERS
        })

        push('/')
    }

    const HandleSideoverPanelClick = (target: 'component' | 'tag' | 'category') => {
        const is_different = target !== PreviousForm.current

        PreviousForm.current = target

        let form: {[key: string]: string | Array<string>} = structuredClone(Form)
        let errors: {[key: string]: boolean} = structuredClone(Errors)

        if (is_different) {
            if (target === 'component') {
                form = COMPONENT_DEFAULT_FORM
                errors = COMPONENT_DEFAULT_ERRORS
            }

            if (target === 'tag') {
                form = TAG_DEFAULT_FORM
                errors = TAG_DEFAULT_ERRORS
            }

            if (target === 'category') {
                form = CATEGORY_DEFAULT_FORM
                errors = CATEGORY_DEFAULT_ERRORS
            }
        }

        SideoverSetState({
            Open: true,
            Target: target,
            Mode: 'create',
            Form: form,
            Errors: errors
        })
    }

    const HandleComponentMode = () => {
        if (pathname === '/') return {
            text: 'Add Component',
            onClick: () => HandleSideoverPanelClick('component')
        }

        if (pathname === '/tags') return {
            text: 'Add Tag',
            onClick: () => HandleSideoverPanelClick('tag')
        }

        if (pathname === '/categories') return {
            text: 'Add Category',
            onClick: () => HandleSideoverPanelClick('category')
        }

        else return {
            text: null,
            onClick: () => {}
        }
    }

    return (
        <nav className='flex items-center'>
            {
                HandleComponentMode().text &&
                <button onClick={() => HandleComponentMode().onClick()} className='select-none flex items-center justify-center w-full px-2 py-1.5 pr-2.5 text-sm font-medium text-[#EBEBEB] rounded-md button-gradient hover:opacity-90 transition-all ease-in-out duration-500'>
                    <PlusIcon className='flex-shrink-0 w-5 h-5 mr-1 text-[#EBEBEB]' />

                    <span className='text-[#EBEBEB] text-[13px] font-bold'>
                        {HandleComponentMode().text}
                    </span>
                </button>
            }

            <div ref={ref} className='select-none flex items-center order-2 relative'>
                <button onClick={() => setOpen(!open)} className='button-gradient p-[1px] select-none flex ml-2 items-center justify-center relative text-sm w-8 h-8 rounded-md md:mr-0 hover:opacity-80 transition-all duration-500 ease-in-out'>
                    <img
                        className='w-7 h-7 rounded-md absolute object-cover full-h z-10'
                        src={User?.Avatar}
                        alt={User?.Name}
                        onError={ImageOnError}
                        onDragStart={ImageOnDrag}
                    />

                    <UserIcon className='w-6 h-6 text-[#EBEBEB]' />
                </button>

                <Dropdown
                    show={open}
                    user={true}
                    links={[
                        {
                            name: 'Logout',
                            onHref: '',
                            onClick: () => {
                                setOpen(false)
                                onLogout()
                            }
                        }
                    ]}
                />
            </div>
        </nav>
    )
}

export default User
